import { useState, useEffect, useRef } from "react";
import { Plus, Trash2, Upload, Phone, Users, FileSpreadsheet, Loader2, Pencil, AlertCircle } from "lucide-react";
import type { CampaignContact, ContactStatus } from "@type/agent";
import {
    getCampaignContacts,
    addCampaignContacts,
    uploadCampaignContacts,
    deleteCampaignContact,
    updateCampaignContact,
} from "@service/agent";
import { contactStatusBadgeConfig, chipVariantStyles, chipDotColor } from "@util/agent-scoring";
import { cn } from "@util/tailwind";
import { Card, CardContent, CardHeader, CardTitle } from "@component/card";
import { Button } from "@component/button";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@component/sheet";
import { format } from "date-fns";

interface ContactsManagerProps {
    campaignId: string;
    onContactCountChange?: (count: number) => void;
    disabled?: boolean;
}

const MAX_CONTACTS = 1000;

export function ContactsManager({ campaignId, onContactCountChange, disabled }: ContactsManagerProps) {
    const [contacts, setContacts] = useState<CampaignContact[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [inputText, setInputText] = useState("");
    const [adding, setAdding] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editingContact, setEditingContact] = useState<CampaignContact | null>(null);
    const [editForm, setEditForm] = useState({ name: "", merchantName: "", context: "" });
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fetchContacts = async () => {
        try {
            const result = await getCampaignContacts(campaignId);
            setContacts(result.contacts);
            setTotalCount(result.total);
            onContactCountChange?.(result.total);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load contacts");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContacts();
    }, [campaignId]);

    const handleAddNumbers = async () => {
        if (!inputText.trim()) return;
        const lines = inputText.split(/[\n,]+/).map((l) => l.trim()).filter((l) => l.length > 0);
        if (lines.length === 0) return;

        setAdding(true);
        setError(null);
        try {
            await addCampaignContacts(campaignId, lines);
            setInputText("");
            await fetchContacts();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to add contacts");
        } finally {
            setAdding(false);
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        setError(null);
        try {
            await uploadCampaignContacts(campaignId, file);
            await fetchContacts();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Upload failed");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleRemoveContact = async (contactId: string) => {
        setError(null);
        try {
            await deleteCampaignContact(campaignId, contactId);
            setContacts((prev) => prev.filter((c) => c.id !== contactId));
            setTotalCount((prev) => {
                const newCount = prev - 1;
                onContactCountChange?.(newCount);
                return newCount;
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to delete contact");
        }
    };

    const handleOpenEdit = (contact: CampaignContact) => {
        setEditingContact(contact);
        setEditForm({
            name: contact.name || "",
            merchantName: contact.merchantName || "",
            context: (contact.extraData?.context) || "",
        });
    };

    const handleSaveEdit = async () => {
        if (!editingContact) return;
        setSaving(true);
        setError(null);
        try {
            await updateCampaignContact(campaignId, editingContact.id, {
                name: editForm.name || undefined,
                merchantName: editForm.merchantName || undefined,
                extraData: { context: editForm.context },
            });
            setContacts((prev) =>
                prev.map((c) =>
                    c.id === editingContact.id
                        ? {
                            ...c,
                            name: editForm.name || undefined,
                            merchantName: editForm.merchantName || undefined,
                            extraData: { ...c.extraData, context: editForm.context },
                        }
                        : c
                )
            );
            setEditingContact(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update contact");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={disabled ? "space-y-6 pointer-events-none opacity-60" : "space-y-6"}>
            {/* Max contacts notice */}
            <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>
                    Contact limit: <span className="font-semibold">{totalCount.toLocaleString()} / {MAX_CONTACTS.toLocaleString()}</span> used
                </span>
            </div>

            <Card>
                <CardHeader><CardTitle>Add Contacts</CardTitle></CardHeader>
                <CardContent>
                    <div className="mb-4">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.csv"
                            onChange={handleFileUpload}
                            className="hidden"
                        />
                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                        >
                            {uploading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <FileSpreadsheet className="w-4 h-4 mr-2" />
                            )}
                            {uploading ? "Uploading..." : "Upload Spreadsheet (.xlsx / .csv)"}
                        </Button>
                        <p className="text-xs text-gray-500 mt-1.5"><span className="font-medium">Required:</span> Phone Number &nbsp;·&nbsp; <span className="text-gray-400">Optional: Name, Merchant Name, Context, and any extra columns</span></p>
                    </div>

                    {error && (
                        <p className="text-xs text-red-600 mb-3">{error}</p>
                    )}

                    <div className="relative mb-4">
                        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                        <div className="relative flex justify-center text-xs"><span className="bg-white dark:bg-gray-950 px-2 text-gray-400">or paste manually</span></div>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Phone Numbers</label>
                        <textarea
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            placeholder={"Paste phone numbers here, one per line or comma-separated...\n\n+6011 1234 5678\n+6017 2244 6666"}
                            className="w-full min-h-[120px] rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-700 resize-none"
                        />
                        <p className="text-xs text-gray-500 mt-1">Enter phone numbers separated by newlines or commas.</p>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                        <p className="text-xs text-gray-500">
                            {inputText.trim()
                                ? `${inputText.split(/[\n,]+/).filter((l) => l.trim()).length} numbers detected`
                                : "No numbers entered"}
                        </p>
                        <Button variant="outline" size="sm" onClick={handleAddNumbers} disabled={!inputText.trim() || adding}>
                            {adding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                            {adding ? "Adding..." : "Add Numbers"}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-gray-400" />
                        <CardTitle>Contact List</CardTitle>
                        <span className="text-sm text-gray-500">({totalCount} contacts)</span>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        </div>
                    ) : contacts.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-100">
                                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Phone Number</th>
                                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Name</th>
                                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Merchant</th>
                                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Context</th>
                                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Status</th>
                                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Added</th>
                                        <th className="w-24 px-4 py-3" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {contacts.map((contact) => (
                                        <tr key={contact.id} className="border-b border-gray-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <Phone className="w-3.5 h-3.5 text-gray-400" />
                                                    <span className="text-sm font-medium text-gray-900 dark:text-white">{contact.phoneNumber}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="text-sm text-gray-600 dark:text-gray-400">{contact.name || "\u2014"}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="text-sm text-gray-600 dark:text-gray-400">{contact.merchantName || "\u2014"}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1">{contact.extraData?.context || "\u2014"}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                {(() => {
                                                    const statusKey = (contact.status || "pending") as ContactStatus;
                                                    const cfg = contactStatusBadgeConfig[statusKey] || contactStatusBadgeConfig.pending;
                                                    return (
                                                        <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border", chipVariantStyles[cfg.variant])}>
                                                            <span className={cn("w-1.5 h-1.5 rounded-full", chipDotColor[cfg.variant], cfg.animated && "animate-pulse")} />
                                                            {cfg.label}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-500">{format(contact.addedAt, "d MMM yyyy")}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => handleOpenEdit(contact)}
                                                        className="text-gray-400 hover:text-blue-500 transition-colors"
                                                        aria-label={`Edit contact ${contact.name || contact.phoneNumber}`}
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleRemoveContact(contact.id)}
                                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                                        aria-label={`Delete contact ${contact.name || contact.phoneNumber}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                            <Upload className="w-10 h-10 mb-3" />
                            <p className="text-sm font-medium text-gray-500">No contacts added yet.</p>
                            <p className="text-xs mt-1 text-gray-400">Use the <span className="font-medium">Add Contacts</span> section above to upload a spreadsheet or paste phone numbers.</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Edit Side Panel */}
            <Sheet open={!!editingContact} onOpenChange={(open) => { if (!open) setEditingContact(null); }}>
                <SheetContent side="right" className="sm:max-w-md">
                    <SheetHeader>
                        <SheetTitle>Edit Contact</SheetTitle>
                        <SheetDescription>
                            Update contact details for {editingContact?.phoneNumber}.
                        </SheetDescription>
                    </SheetHeader>
                    <div className="mt-6 space-y-4">
                        <div>
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number</label>
                                <span className="text-xs text-gray-400">(cannot be changed)</span>
                            </div>
                            <div className="h-9 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm flex items-center text-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400">
                                {editingContact?.phoneNumber}
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Name</label>
                            <input
                                value={editForm.name}
                                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                                className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm dark:bg-gray-900 dark:border-gray-700"
                                placeholder="Contact name"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Merchant</label>
                            <input
                                value={editForm.merchantName}
                                onChange={(e) => setEditForm((f) => ({ ...f, merchantName: e.target.value }))}
                                className="w-full h-9 rounded-md border border-gray-200 bg-white px-3 text-sm dark:bg-gray-900 dark:border-gray-700"
                                placeholder="Merchant name"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">Context</label>
                            <textarea
                                value={editForm.context}
                                onChange={(e) => setEditForm((f) => ({ ...f, context: e.target.value.slice(0, 1000) }))}
                                rows={6}
                                maxLength={1000}
                                className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-700 resize-none"
                                placeholder="Additional context for this contact..."
                            />
                            <p className="text-xs text-gray-400 mt-1 text-right">{editForm.context.length}/1000</p>
                        </div>
                        <div className="flex items-center gap-3 pt-4">
                            <Button onClick={handleSaveEdit} disabled={saving} className="flex-1">
                                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                                {saving ? "Saving..." : "Save Changes"}
                            </Button>
                            <Button variant="outline" onClick={() => setEditingContact(null)} disabled={saving}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}
